import { formatImageId } from '@/lib/waypoint-image'
import markupLib from '@/lib/markup'
import Color from '@/lib/color'

import { controlState } from '@/services/control-layout-service'
import { getItem } from '@/plugins/storage'
import { v4 as uuid } from 'uuid'
import { findArea, getBestFitUnitOfLength } from '@/lib/math'
import { getCenter, getCenterOfBounds, getDistance } from 'geolib'
import markupService from '@/services/markup-service'

/**
 * Lib to transform markup objects from the markup service, to geoJson features for Mapbox to consume
 */

const DEFAULT_STYLES = {
  line: {
    color: [255, 51, 0, 1],
    weight: 'normal',
    style: 'solid',
  },
  track: { color: [0, 255, 255, 1], weight: 'normal', style: 'dash' },
  shape: {
    color: [255, 51, 0, 1],
    weight: 'normal',
    style: 'solid',
    fillColor: [74, 144, 226, 0.1],
  },
  waypoint: {},
}

/**
 * Given a markup object from the markup service, return a getJson feature to
 * display in Mapbox
 *
 * @param {Object} markup - markup object from the markup service
 * @param {Boolean} highlighted - whether or not the markup should be highlighted
 * @returns {GeoJson}
 */
export const generateMarkupGeoJson = (markup, highlighted) => {
  const geoJSON = markup.toGeoJSON()
  const properties = markup.properties ?? {}
  const formattedGeoJson = {
    ...geoJSON,
    id: markup.uuid,
    properties: {
      // Set default styles first and then override with markup styles
      ...DEFAULT_STYLES[markup.type],
      ...geoJSON.properties,
      ...properties,
      id: markup.uuid,
      type: markup.type,
      highlighted,
      selectable: true,
    },
  }

  // Remove point intervals from track geoJson
  if (markup.type === 'track' && formattedGeoJson.properties.point_intervals) {
    delete formattedGeoJson.properties.point_intervals
  }

  if (markup.type === 'waypoint') {
    const icon = geoJSON?.properties?.icon ?? 'Location'
    /* There's some waypoints in the wild that are missing color, just display
       the default */
    let color
    if (markup.color) {
      // Waypoint color is an RGBA object
      color = Color.fromRgbaObject(markup.color)
    } else {
      color = markupLib.getDefaultColor('fish').color
    }
    formattedGeoJson.properties.icon_image = formatImageId(
      icon,
      color,
      highlighted
    )
    formattedGeoJson.properties.icon_image_low_zoom = formatImageId(
      icon,
      color,
      highlighted,
      true
    )
  }

  return formattedGeoJson
}

/**
 * Given a waypoint object from the markup service, return a geoJson feature for a waypoint name
 * label to display in Mapbox
 *
 * @param {Object} waypoint - waypoint object from markup service
 * @returns {Array<GeoJSON>}
 */
export const generateWaypointLabelGeoJson = (waypoint) => {
  // Only add label if the name is not the default name we provide
  const name = waypoint.name.trim()
  if (
    !controlState().namesOnWaypoints ||
    !markupService.isCustomMarkupName(name)
  ) {
    return []
  }

  return [
    {
      id: uuid(),
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: waypoint.toGeoJSON().geometry.coordinates,
      },
      properties: {
        title: name.length > 50 ? `${name.slice(0, 50)}...` : name,
        meta: 'waypoint-label',
        labelFor: waypoint.uuid,
      },
    },
  ]
}

/**
 * Given a shape object from the markup service, return a geoJson feature for the line
 * distance and area labels to display in Mapbox
 *
 * @param {Object} waypoint - waypoint object from markup service
 * @returns {Array<GeoJSON>}
 */
export const generateShapeLabelsGeoJson = (shape) => {
  const unitType = getItem('unitType')
  const coordinates = shape.toGeoJSON().geometry.coordinates[0]

  if (!coordinates?.length) {
    return []
  }

  const labels = []

  if (!shape.toGeoJSON().properties.free_hand) {
    for (let i = 0; i < coordinates.length - 1; i++) {
      const coords = [coordinates[i], coordinates[i + 1]]
      const distance = getDistance(...coords)
      const center = getCenter(coords)
      const label = {
        id: uuid(),
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [center.longitude, center.latitude],
        },
        properties: {
          title: getBestFitUnitOfLength({
            length: distance,
            from: 'meters',
            unitType,
          }),
          meta: 'label',
          labelFor: shape.uuid,
        },
      }
      labels.push(label)
    }
  }

  const area = findArea(coordinates, unitType)
  const center = getCenterOfBounds(coordinates)
  const areaLabel = {
    id: uuid(),
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [center.longitude, center.latitude],
    },
    properties: {
      title: `${area.area}${area.UOM}`,
      meta: 'label',
      labelFor: shape.uuid,
    },
  }
  labels.push(areaLabel)

  return labels
}
